import { DataTableQueryContextProvider } from "@/components/data-table/DataTableQueryContext";
import { RowSelectionProvider } from "@/components/data-table/context/RowSelectionProvider";
import { useRowSelectionContext } from "@/components/data-table/context/RowSelectionContext";
import { List } from "@/components/list/List";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useProjectManagersAddUsersContext } from "@/modules/projects/components/managers/ProjectManagersAddUsersContext";
import { ProjectManagersAddUsersSubmitButton } from "@/modules/projects/components/managers/ProjectManagersAddUsersSubmitButton";
import { memo, useCallback, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useProjectId } from "@/modules/projects/hooks/useProjectId";
import { DisplayProjectManager } from "@/types/projectManager";
import { displayProjectManager } from "@/schemas/projectManager";
import { useProjectManagersAddUsersForm } from "@/modules/projects/hooks/useProjectManagersAddUsersForm";
import { useProjectManagersAvailableQuery } from "@/modules/projects/hooks/useProjectManagersAvailableQuery";

const ProjectManagersAddUsersDialogContent = () => {
  const { t } = useTranslation();
  const { store } = useRowSelectionContext();
  const {
    isAddUsersDialogOpened,
    setIsAddUsersDialogOpened,
    managersDirectoryQueryKey,
  } = useProjectManagersAddUsersContext();
  const projectId = useProjectId();
  const { query, options } = useProjectManagersAvailableQuery({
    projectId,
    enabled: isAddUsersDialogOpened,
  });

  const handleSuccess = useCallback(() => {
    setIsAddUsersDialogOpened(false);
  }, [setIsAddUsersDialogOpened]);

  const { handleSubmit, isSubmitting } = useProjectManagersAddUsersForm({
    store,
    projectId,
    onSuccess: handleSuccess,
    managersDirectoryQueryKey,
  });

  useEffect(() => {
    // Reset the selection when the dialog is closed
    if (!isAddUsersDialogOpened) {
      store.clear();
    }
  }, [isAddUsersDialogOpened, store]);

  const onOpenChange = useCallback(
    (open: boolean) => {
      setIsAddUsersDialogOpened(open);
    },
    [setIsAddUsersDialogOpened],
  );

  const tableFeatures = useMemo(
    () => ({
      enableRowSelection: true,
      enableSelectAll: true,
      enableFilters: false,
      enableSorting: true,
    }),
    [],
  );

  return (
    <Dialog open={isAddUsersDialogOpened} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[90dvw] h-[95dvh]">
        <DataTableQueryContextProvider value={{ query }}>
          <List<DisplayProjectManager>
            schema={displayProjectManager}
            name="managers"
            query={query}
            options={options}
            tableFeatures={tableFeatures}
          >
            <div className="flex flex-col gap-3 min-h-0 min-w-0">
              <DialogHeader>
                <DialogTitle className="text-xl font-medium text-gray-800">
                  {t("projects.managers.addUsers.title")}
                </DialogTitle>
              </DialogHeader>
              <div className="flex flex-row items-center gap-2">
                <List.Menubar className="m-0 grow">
                  <List.Menubar.Tools inDialog />
                  <List.Menubar.Columns inDialog />
                  <List.Menubar.Reset inDialog />
                </List.Menubar>
              </div>
              <div className="flex flex-row grow gap-2 overflow-hidden">
                <List.Content className="overflow-x-auto flex-grow" isInDialog>
                  <List.Status>
                    <List.SelectedRowsStatus />
                  </List.Status>
                </List.Content>
              </div>
              <DialogFooter className="gap-3">
                <DialogClose asChild>
                  <Button variant="link" disabled={isSubmitting}>
                    {t("projects.managers.addUsers.cancel")}
                  </Button>
                </DialogClose>
                <ProjectManagersAddUsersSubmitButton
                  onSubmit={handleSubmit}
                  isSubmitting={isSubmitting}
                />
              </DialogFooter>
            </div>
          </List>
        </DataTableQueryContextProvider>
      </DialogContent>
    </Dialog>
  );
};

const ProjectManagersAddUsersDialogComponent = () => {
  return (
    <RowSelectionProvider>
      <ProjectManagersAddUsersDialogContent />
    </RowSelectionProvider>
  );
};

export const ProjectManagersAddUsersDialog = memo(
  ProjectManagersAddUsersDialogComponent,
);
